<template>
	<div
		ref="root"
		class="receipt-viewer"
		:class="{ 'receipt-viewer--fullscreen': isFullscreen }"
		data-test="receipt-viewer"
		tabindex="0"
		@keydown="onKeydown"
	>
		<!-- Bedienleiste: drei Gruppen, die im schmalen Panel als Ganzes umbrechen -->
		<div class="receipt-viewer__bar">
			<div class="receipt-viewer__group">
				<ButtonIcon
					:disabled="!ready || zoom <= MIN_ZOOM"
					:tooltip="{ text: t(`${I18N}.zoom_out`) }"
					data-test="receipt-viewer-zoom-out"
					density="comfortable"
					icon="fal fa-magnifying-glass-minus"
					notext
					size="small"
					variant="text"
					@click="zoomBy(1 / ZOOM_STEP)"
				></ButtonIcon>
				<v-btn
					:disabled="!ready"
					class="receipt-viewer__zoom"
					data-test="receipt-viewer-zoom"
					density="comfortable"
					size="small"
					variant="text"
					@click="fit()"
				>
					{{ Math.round(zoom * 100) }}%
				</v-btn>
				<ButtonIcon
					:disabled="!ready || zoom >= MAX_ZOOM"
					:tooltip="{ text: t(`${I18N}.zoom_in`) }"
					data-test="receipt-viewer-zoom-in"
					density="comfortable"
					icon="fal fa-magnifying-glass-plus"
					notext
					size="small"
					variant="text"
					@click="zoomBy(ZOOM_STEP)"
				></ButtonIcon>
			</div>

			<div class="receipt-viewer__group">
				<ButtonIcon
					:disabled="!ready"
					:tooltip="{ text: t(`${I18N}.rotate`) }"
					data-test="receipt-viewer-rotate"
					density="comfortable"
					icon="fal fa-rotate-right"
					notext
					size="small"
					variant="text"
					@click="rotate()"
				></ButtonIcon>
				<ButtonIcon
					v-if="fullscreenSupported"
					:tooltip="{
						text: t(
							`${I18N}.${isFullscreen ? 'fullscreen_exit' : 'fullscreen'}`
						),
					}"
					data-test="receipt-viewer-fullscreen"
					density="comfortable"
					:icon="isFullscreen ? 'fal fa-compress' : 'fal fa-expand'"
					notext
					size="small"
					variant="text"
					@click="toggleFullscreen"
				></ButtonIcon>
				<ButtonIcon
					:disabled="!blob"
					:tooltip="{ text: t(`${I18N}.download`) }"
					data-test="receipt-viewer-download"
					density="comfortable"
					icon="fal fa-download"
					notext
					size="small"
					variant="text"
					@click="blob && downloadBlob(blob, fileName)"
				></ButtonIcon>
				<ButtonIcon
					:disabled="!blob"
					:tooltip="{ text: t(`${I18N}.print`) }"
					data-test="receipt-viewer-print"
					density="comfortable"
					icon="fal fa-print"
					notext
					size="small"
					variant="text"
					@click="blob && printPdf(blob)"
				></ButtonIcon>
			</div>

			<div
				v-if="pageCount > 1"
				class="receipt-viewer__group receipt-viewer__group--pages"
			>
				<ButtonIcon
					:disabled="currentPage <= 1"
					:tooltip="{ text: t(`${I18N}.prev_page`) }"
					data-test="receipt-viewer-prev"
					density="comfortable"
					icon="fal fa-chevron-up"
					notext
					size="small"
					variant="text"
					@click="goToPage(currentPage - 1)"
				></ButtonIcon>
				<span
					class="receipt-viewer__page-label text-caption"
					data-test="receipt-viewer-page"
				>
					{{ currentPage }} / {{ pageCount }}
				</span>
				<ButtonIcon
					:disabled="currentPage >= pageCount"
					:tooltip="{ text: t(`${I18N}.next_page`) }"
					data-test="receipt-viewer-next"
					density="comfortable"
					icon="fal fa-chevron-down"
					notext
					size="small"
					variant="text"
					@click="goToPage(currentPage + 1)"
				></ButtonIcon>
			</div>
		</div>

		<!-- Leuchttisch -->
		<div
			ref="stage"
			class="receipt-viewer__stage"
			:class="{ 'receipt-viewer__stage--pannable': pannable }"
			@dblclick="onDblClick"
			@pointerdown="onPointerDown"
			@pointermove="onPointerMove"
			@pointerup="onPointerUp"
			@pointercancel="onPointerUp"
			@scroll.passive="onScroll"
			@wheel="onWheel"
		>
			<Loading
				v-if="loading"
				:progress="progress"
				:text="t(`${I18N}.loading`)"
				data-test="receipt-viewer"
			></Loading>

			<div
				v-else-if="failed"
				class="receipt-viewer__error text-medium-emphasis"
				data-test="receipt-viewer-error"
			>
				<v-icon icon="fal fa-file-slash" size="x-large"></v-icon>
				<span class="text-caption">{{ t(`${I18N}.load_error`) }}</span>
			</div>

			<div v-show="ready" class="receipt-viewer__pages">
				<div
					v-for="(dim, index) in pageDims"
					:key="index"
					:ref="(el) => (pageEls[index] = el as HTMLElement | null)"
					class="receipt-viewer__page-box"
					:style="boxStyle(dim)"
				>
					<img
						v-if="imageUrl"
						:src="imageUrl"
						:style="pageStyle(dim)"
						alt=""
						class="receipt-viewer__page"
						draggable="false"
					/>
					<canvas
						v-else
						:ref="(el) => (canvasEls[index] = el as HTMLCanvasElement | null)"
						:style="pageStyle(dim)"
						class="receipt-viewer__page"
					></canvas>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
/* global document, window, console, AbortController, AbortSignal, Blob, URL, Image, HTMLElement, HTMLCanvasElement, PointerEvent, WheelEvent, MouseEvent, KeyboardEvent */
/**
 * Belegbetrachter fuer das Pruef-Cockpit (Seitenpanel + Fokus-Modus).
 *
 * Bewusst getrennt von templates/PdfViewer.vue: der teilt sich mit Protokoll
 * und Dokumentenansicht eine Karte samt Fussleiste, die im engen Cockpit-
 * Panel keinen Platz hat. Hier: eine 36px-Leiste, darunter der Beleg.
 *
 * PDF-Seiten werden EINMAL in hoher Aufloesung gerendert; Zoom und Drehung
 * laufen rein ueber CSS (Breite + transform), also ohne Nachrendern.
 */
import { useDocumentStore } from '@/stores/modules/document'
import { useElementSize, useLocalStorage } from '@vueuse/core'
import useFileHandler from 'composables/useFileHandler.ts'
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist'
import { getDocument } from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.mjs'
import ButtonIcon from 'templates/Base/ButtonIcon.vue'
import Loading from 'templates/Loading.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
	documentId: number
	kind: 'pdf' | 'img'
	fileName: string
}>()

const I18N = 'component.reviewDocuments.cockpit.receipt.viewer'
const MIN_ZOOM = 0.2
const MAX_ZOOM = 5
const ZOOM_STEP = 1.25
// Wie templates/PdfViewer.vue: scharf bis ~1,5x Einpassen, Speicher bleibt
// bei A4 unter 20 MB je Seite.
const RENDER_SCALE = Math.min(
	Math.max((window.devicePixelRatio || 1) * 1.5, 1.5),
	3
)
// Siehe PdfViewer.vue — ohne diese Assets bleiben gescannte PDFs leer.
const PDFJS_ASSET_BASE = `${import.meta.env.BASE_URL}pdfjs/`

const { t } = useI18n()
const documentStore = useDocumentStore()
const { printPdf, downloadBlob } = useFileHandler()

const root = ref<HTMLElement | null>(null)
const stage = ref<HTMLElement | null>(null)
const { width: stageWidth, height: stageHeight } = useElementSize(stage)
const pageEls = ref<(HTMLElement | null)[]>([])
const canvasEls = ref<(HTMLCanvasElement | null)[]>([])

const loading = ref(true)
const failed = ref(false)
const progress = ref(0)
const blob = ref<Blob>()
const imageUrl = ref<string>()
/** Seitenmasse in CSS-px bei Zoom 1 (PDF: pt, Bild: natuerliche Pixel). */
const pageDims = ref<{ width: number; height: number }[]>([])
const zoom = ref(1)
const fitMode = ref(true)
const currentPage = ref(1)
const isFullscreen = ref(false)
const fullscreenSupported = !!document.documentElement.requestFullscreen

/** Drehstufe je Beleg, ueberlebt den Wechsel zur naechsten Buchung. */
const storedRotations = useLocalStorage<Record<number, number>>(
	'cockpit.receiptRotation',
	{}
)
const rotation = computed(() => storedRotations.value[props.documentId] ?? 0)

const ready = computed(() => !loading.value && !failed.value)
const pageCount = computed(() => pageDims.value.length)
const swapped = computed(() => rotation.value % 180 !== 0)

// pdf.js 5+: aufraeumen geht nur ueber den LoadingTask, nicht das Dokument.
let pdfTask: PDFDocumentLoadingTask | null = null
let abort: AbortController | null = null

// --- Laden ------------------------------------------------------------------

const load = async () => {
	abort?.abort()
	abort = new AbortController()
	const { signal } = abort
	loading.value = true
	failed.value = false
	progress.value = 0

	try {
		const { data } = await documentStore.documentBinary({
			id: props.documentId,
			signal,
			onDownloadProgress: (e) => {
				if (e.total) progress.value = Math.round((e.loaded / e.total) * 90)
			},
		})
		if (signal.aborted) return
		blob.value = data
		if (props.kind === 'img') {
			await loadImage(data)
		} else {
			await loadPdf(data, signal)
		}
		if (signal.aborted) return
		zoom.value = 1
		fitMode.value = true
		currentPage.value = 1
		loading.value = false
		await nextTick()
		fit()
		stage.value?.scrollTo(0, 0)
	} catch (error: any) {
		// Belegwechsel/Unmount mitten im Laden: der abgebrochene Lauf darf den
		// Zustand des neuen nicht mehr anfassen (sonst "konnte nicht geladen").
		if (signal.aborted) return
		if (error?.name === 'CanceledError' || error?.name === 'AbortError') return
		console.error('Error loading receipt:', error)
		failed.value = true
		loading.value = false
	}
}

const loadImage = (data: Blob) =>
	new Promise<void>((resolve, reject) => {
		const url = URL.createObjectURL(data)
		const img = new Image()
		img.onload = () => {
			imageUrl.value = url
			pageDims.value = [{ width: img.naturalWidth, height: img.naturalHeight }]
			resolve()
		}
		img.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error('image decode failed'))
		}
		img.src = url
	})

const loadPdf = async (data: Blob, signal: AbortSignal) => {
	const buffer = await data.arrayBuffer()
	if (signal.aborted) return
	const task = getDocument({
		data: buffer,
		wasmUrl: `${PDFJS_ASSET_BASE}wasm/`,
		cMapUrl: `${PDFJS_ASSET_BASE}cmaps/`,
		cMapPacked: true,
		standardFontDataUrl: `${PDFJS_ASSET_BASE}standard_fonts/`,
	})
	pdfTask = task
	const pdf: PDFDocumentProxy = await task.promise
	if (signal.aborted) return

	const pages = await Promise.all(
		Array.from({ length: pdf.numPages }, (_, i) => pdf.getPage(i + 1))
	)
	if (signal.aborted) return
	pageDims.value = pages.map((p) => {
		const { width, height } = p.getViewport({ scale: 1 })
		return { width, height }
	})
	await nextTick()

	// Erste Seite abwarten, damit der Ladebalken bis zum ersten Bild steht;
	// der Rest rendert im Hintergrund waehrend der Nutzer schon liest.
	const render = async (i: number) => {
		const canvas = canvasEls.value[i]
		if (!canvas || signal.aborted) return
		const viewport = pages[i].getViewport({ scale: RENDER_SCALE })
		canvas.width = viewport.width
		canvas.height = viewport.height
		await pages[i].render({ canvasContext: canvas.getContext('2d')!, viewport })
			.promise
		progress.value = 90 + Math.round(((i + 1) / pages.length) * 10)
	}
	await render(0)
	void (async () => {
		try {
			for (let i = 1; i < pages.length; i++) await render(i)
		} catch (error: any) {
			// Abbruch beim Belegwechsel ist kein Fehler
			if (error?.name !== 'RenderingCancelledException' && !signal.aborted) {
				console.error('Error rendering receipt page:', error)
			}
		}
	})()
}

// --- Geometrie --------------------------------------------------------------

/** Aeussere Box: nimmt bei 90/270 Grad die getauschten Masse ein. */
const boxStyle = (dim: { width: number; height: number }) => {
	const w = (swapped.value ? dim.height : dim.width) * zoom.value
	const h = (swapped.value ? dim.width : dim.height) * zoom.value
	return { width: `${w}px`, height: `${h}px` }
}

/** Seite selbst: unrotierte Masse, um die eigene Mitte gedreht. */
const pageStyle = (dim: { width: number; height: number }) => ({
	width: `${dim.width * zoom.value}px`,
	height: `${dim.height * zoom.value}px`,
	transform: `translate(-50%, -50%) rotate(${rotation.value}deg)`,
})

/*
 * Ganze Seite statt nur Seitenbreite. Auf Breite eingepasst ragte eine
 * hochformatige Seite fast immer unten heraus: die Bühne bekam ihren eigenen
 * Rollbalken, und weil die Detailspalte daneben schon einen hat, standen
 * regelmässig zwei nebeneinander. Mit dem Kleineren beider Faktoren passt die
 * Seite in beide Richtungen und der innere Balken verschwindet — bei einem
 * einseitigen Beleg ganz, bei mehreren bleibt das Blättern übrig, was er ja
 * auch soll.
 *
 * Gemessen wird gegen die grösste Seite, nicht gegen die Summe: bei einem
 * zehnseitigen PDF wäre die Summe der Höhen sonst der Massstab und der Zoom
 * fiele auf ein Zehntel.
 */
const fitZoom = () => {
	const widest = Math.max(
		...pageDims.value.map((d) => (swapped.value ? d.height : d.width)),
		1
	)
	const tallest = Math.max(
		...pageDims.value.map((d) => (swapped.value ? d.width : d.height)),
		1
	)
	// stageWidth/-Height sind die Content-Box (ohne Polsterung und Rollbalken);
	// 2px Luft fuer den Seitenrahmen.
	const byWidth = (stageWidth.value - 2) / widest
	/*
	 * Vor dem ersten Layout ist die Hoehe 0 — dann zaehlt nur die Breite,
	 * sonst faellt der Zoom auf MIN_ZOOM und die Seite waere kurz briefmarken-
	 * gross, bevor der Watcher sie wieder aufzieht.
	 */
	const byHeight =
		stageHeight.value > 0 ? (stageHeight.value - 2) / tallest : byWidth
	return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.min(byWidth, byHeight)))
}

const fit = () => {
	fitMode.value = true
	zoom.value = fitZoom()
}

/**
 * Zoomt um einen Faktor und haelt dabei den Punkt unter dem Cursor (oder
 * die Mitte) fest, damit der Ausschnitt nicht wegspringt.
 */
const zoomBy = (factor: number, anchor?: { x: number; y: number }) => {
	const el = stage.value
	if (!el) return
	const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom.value * factor))
	const ratio = next / zoom.value
	if (ratio === 1) return
	const ax = anchor?.x ?? el.clientWidth / 2
	const ay = anchor?.y ?? el.clientHeight / 2
	const sx = el.scrollLeft
	const sy = el.scrollTop
	fitMode.value = false
	zoom.value = next
	nextTick(() => {
		el.scrollLeft = (sx + ax) * ratio - ax
		el.scrollTop = (sy + ay) * ratio - ay
	})
}

const rotate = () => {
	const next = (rotation.value + 90) % 360
	if (next === 0) {
		delete storedRotations.value[props.documentId]
	} else {
		storedRotations.value[props.documentId] = next
	}
	if (fitMode.value) nextTick(fit)
}

const goToPage = (page: number) => {
	const el = pageEls.value[page - 1]
	if (!el || !stage.value) return
	stage.value.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' })
}

watch([stageWidth, stageHeight], () => {
	if (fitMode.value && ready.value) zoom.value = fitZoom()
})

// --- Interaktion ------------------------------------------------------------

const pannable = computed(
	() => ready.value && !fitMode.value && zoom.value > fitZoom()
)
let drag: { x: number; y: number; sx: number; sy: number } | null = null

const onPointerDown = (e: PointerEvent) => {
	if (!pannable.value || e.button !== 0) return
	const el = stage.value!
	drag = { x: e.clientX, y: e.clientY, sx: el.scrollLeft, sy: el.scrollTop }
	el.setPointerCapture(e.pointerId)
}
const onPointerMove = (e: PointerEvent) => {
	if (!drag) return
	const el = stage.value!
	el.scrollLeft = drag.sx - (e.clientX - drag.x)
	el.scrollTop = drag.sy - (e.clientY - drag.y)
}
const onPointerUp = (e: PointerEvent) => {
	if (!drag) return
	drag = null
	stage.value?.releasePointerCapture(e.pointerId)
}

const onWheel = (e: WheelEvent) => {
	// Nur Strg/Cmd+Rad zoomt — nacktes Rad scrollt wie gewohnt.
	if (!e.ctrlKey && !e.metaKey) return
	e.preventDefault()
	if (!ready.value) return
	const rect = stage.value!.getBoundingClientRect()
	zoomBy(Math.pow(ZOOM_STEP, -e.deltaY / 100), {
		x: e.clientX - rect.left,
		y: e.clientY - rect.top,
	})
}

const onDblClick = (e: MouseEvent) => {
	if (!ready.value) return
	const rect = stage.value!.getBoundingClientRect()
	const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top }
	if (fitMode.value) {
		zoomBy(1 / zoom.value, anchor)
	} else {
		fit()
	}
}

const onScroll = () => {
	const el = stage.value
	if (!el || pageCount.value < 2) return
	// Seite, die die Mitte des Sichtfensters enthaelt
	const mid = el.scrollTop + el.clientHeight / 2
	const idx = pageEls.value.findIndex(
		(p) => p && p.offsetTop + p.offsetHeight >= mid
	)
	if (idx >= 0) currentPage.value = idx + 1
}

const onKeydown = (e: KeyboardEvent) => {
	if (!ready.value) return
	const actions: Record<string, () => void> = {
		ArrowLeft: () => goToPage(currentPage.value - 1),
		ArrowRight: () => goToPage(currentPage.value + 1),
		PageUp: () => goToPage(currentPage.value - 1),
		PageDown: () => goToPage(currentPage.value + 1),
		'+': () => zoomBy(ZOOM_STEP),
		'-': () => zoomBy(1 / ZOOM_STEP),
		'0': fit,
		r: rotate,
		R: rotate,
	}
	const action = actions[e.key]
	if (!action) return
	e.preventDefault()
	action()
}

const toggleFullscreen = () => {
	if (document.fullscreenElement === root.value) {
		document.exitFullscreen()
	} else {
		root.value?.requestFullscreen()
	}
}
const onFullscreenChange = () => {
	isFullscreen.value = document.fullscreenElement === root.value
}

// --- Lebenszyklus -----------------------------------------------------------

const teardown = () => {
	abort?.abort()
	// bricht auch laufende render()-Aufrufe ab (RenderingCancelledException)
	pdfTask?.destroy()
	pdfTask = null
	if (imageUrl.value) URL.revokeObjectURL(imageUrl.value)
	imageUrl.value = undefined
	pageDims.value = []
	blob.value = undefined
}

onMounted(() => {
	document.addEventListener('fullscreenchange', onFullscreenChange)
	load()
})

watch(
	() => props.documentId,
	() => {
		teardown()
		load()
	}
)

onBeforeUnmount(() => {
	document.removeEventListener('fullscreenchange', onFullscreenChange)
	teardown()
})
</script>

<style lang="scss" scoped>
.receipt-viewer {
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-radius: 4px;
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	outline: none;
	overflow: hidden;
	width: 100%;
}

.receipt-viewer--fullscreen {
	border: none;
	border-radius: 0;
}

.receipt-viewer__bar {
	align-items: center;
	border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	display: flex;
	flex: 0 0 auto;
	flex-wrap: wrap;
	gap: 0 6px;
	min-height: 36px;
	padding: 2px 4px;
}

.receipt-viewer__group {
	align-items: center;
	display: flex;
	flex: 0 0 auto;
	gap: 2px;
}

.receipt-viewer__group--pages {
	margin-left: auto;
}

.receipt-viewer__zoom {
	font-variant-numeric: tabular-nums;
	min-width: 52px;
}

.receipt-viewer__page-label {
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
}

.receipt-viewer__stage {
	background: var(--tenant-surface-1);
	flex: 1 1 auto;
	min-height: 0;
	overflow: auto;
	overscroll-behavior: contain;
	padding: 8px;
	position: relative;
}

.receipt-viewer__stage--pannable {
	cursor: grab;
	user-select: none;

	&:active {
		cursor: grabbing;
	}
}

.receipt-viewer__pages {
	align-items: center;
	display: flex;
	flex-direction: column;
	gap: 12px;
	/* Breiter als der Leuchttisch: nicht zentrieren, sonst ist links abgeschnitten */
	min-width: fit-content;
}

.receipt-viewer__page-box {
	flex: 0 0 auto;
	position: relative;
}

/* Flat: Rahmen statt Schatten */
.receipt-viewer__page {
	background: #fff;
	border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
	box-sizing: border-box;
	display: block;
	left: 50%;
	position: absolute;
	top: 50%;
}

.receipt-viewer__error {
	align-items: center;
	display: flex;
	flex-direction: column;
	gap: 8px;
	height: 100%;
	justify-content: center;
	text-align: center;
}
</style>
